import Phaser from 'phaser';

import { GameSizeMetrics } from 'src/games/common/phaser/game/scenes/scaled-base-scene';
import { BlocksMainScene } from '../../scenes/blocks-main-scene';
import { StartViewModel } from './start-view-model';
import { Subscription } from 'rxjs';
import { TextResizer } from '../grid/text-resizer';

export class StartView extends Phaser.GameObjects.Container {
  private subscriptionGroup: Subscription;
  private textResizer: TextResizer;

  private startButton: Phaser.GameObjects.Image;
  private buttonIcon: Phaser.GameObjects.Image;
  private buttonLabel: Phaser.GameObjects.Text;
  private buttonAnimation: Phaser.Tweens.Tween;

  constructor(
    scene: BlocksMainScene,
    x: number,
    y: number,
    private viewModel: StartViewModel,
  ) {
    super(scene, x, y);

    this.subscriptionGroup = new Subscription();
    this.textResizer = new TextResizer(scene, this, {
      fontFamily: 'Inter',
      fontSize: '50px',
      fontStyle: 'bold',
      color: '#121212',
    });

    this.viewModel = viewModel;

    this.startButton = scene.add
      .image(0, 0, viewModel.buttonAsset.type)
      .setOrigin(0.5, 0.5)
      .setInteractive();
    this.add([this.startButton]);

    this.buttonIcon = scene.add.image(0, 0, viewModel.buttonIcon.type).setOrigin(0.5, 0.5);
    this.add([this.buttonIcon]);

    this.buttonLabel = this.scene.add
      .text(0, 0, this.viewModel.playLabelText, {
        fontFamily: 'Inter',
        fontSize: '50px',
        fontStyle: 'bold',
        color: '#121212',
      })
      .setOrigin(0.5, 0.5);
    this.add([this.buttonLabel]);

    this.startButton.on(Phaser.Input.Events.POINTER_UP, (_pointer: Phaser.Input.Pointer) => {
      viewModel.startClicked();
      this.showButton(false);
    });

    this.setName(StartView.name);
    scene.add.existing(this);

    this.buttonAnimation = scene.tweens.add({
      targets: this,
      scaleX: 1.1,
      scaleY: 1.1,
      yoyo: true,
      repeat: -1, // Infinite
      duration: 1200,
      ease: 'Sine.easeInOut',
      paused: false,
    });

    this.subscriptionGroup.add(
      viewModel.onShowStartButtonTrigger?.subscribe((show) => {
        this.showButton(show);
      }),
    );
  }

  override destroy(_fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    super.destroy(_fromScene);
  }

  public onResize(gameSizeMetrics: GameSizeMetrics) {
    const layoutConfig = this.viewModel.layoutConfig;
    const calc = this.viewModel.calculateScaleFactor(gameSizeMetrics);

    const cellSz = layoutConfig.boardHeight / 9;
    const shapeBoardHeight = cellSz * 3;

    const xPos = layoutConfig.hMargin + layoutConfig.boardWidth * 0.5;
    const yPos = layoutConfig.vMargin + shapeBoardHeight * 0.5 + layoutConfig.boardHeight;

    this.setPosition(xPos * calc.scaleFactor + calc.scaledOffsetX, yPos * calc.scaleFactor);
    this.setSize(layoutConfig.boardWidth * calc.scaleFactor, shapeBoardHeight * calc.scaleFactor);

    const buttonScale = 1.0;
    this.startButton.setDisplaySize(
      this.viewModel.buttonAsset.dimensions.width * calc.scaleFactor * buttonScale,
      this.viewModel.buttonAsset.dimensions.height * calc.scaleFactor * buttonScale,
    );

    const iconScale = 0.5;
    this.buttonIcon.setDisplaySize(
      this.viewModel.buttonIcon.dimensions.width * calc.scaleFactor * iconScale,
      this.viewModel.buttonIcon.dimensions.height * calc.scaleFactor * iconScale,
    );

    const availableWidth = (this.startButton.displayWidth - this.buttonIcon.displayWidth) * 0.6;

    const fontSize = this.textResizer.fitText('XXXXXXXXX', availableWidth, 14);
    this.buttonLabel.setFontSize(fontSize);

    const spacing = this.buttonIcon.displayWidth * 0.2;
    this.buttonIcon.setX(-spacing * 0.5 - this.buttonLabel.displayWidth * 0.5);
    this.buttonLabel.setX(spacing * 0.5 + this.buttonIcon.displayWidth * 0.5);
  }

  private showButton(show: boolean) {
    this.visible = show;
    if (show) {
      this.startButton.setInteractive();
      this.buttonAnimation.play();
    } else {
      this.startButton.disableInteractive();
      this.buttonAnimation.stop();
      this.setScale(1.0);
    }
  }
}
